'use client'

import type { AllListingsFilters } from '@b-stock/search-api-client'

import { regionFilterContribution } from '@/components/listings/search/SearchFilters/shared/regionFilterUtils'

import type { DateRange, Range } from '../SearchFilters/shared/commonTypes'
import { isRange, isStringList } from './typeInspectors'
import type { ListingFiltersState } from './types'

const filterContribution = (value: string[]) => value.length
const rangeContribution = ([min, max]: Range | DateRange) =>
  min === null && max === null ? 0 : 1
const distanceFilterContribution = (value: string | null) =>
  value === null || value === '' ? 0 : 1
const searchQueryFilterContribution = (value: string | null) =>
  value === null ? 0 : 1
const numericValueFilterContribution = (value: number | null) =>
  value === null ? 0 : 1
const booleanFilterContribution = (value: boolean | undefined) =>
  value === true ? 1 : 0

const calculateActiveFilterCount = (
  filters: ListingFiltersState,
  allFilters: AllListingsFilters | undefined
): number => {
  return (Object.keys(filters) as (keyof ListingFiltersState)[]).reduce(
    (acc: number, key): number => {
      if (key === 'region') {
        return acc + regionFilterContribution(filters[key], allFilters?.region)
      }
      if (key === 'distanceRange') {
        return acc + distanceFilterContribution(filters[key].zipCode)
      }
      if (key === 'q') {
        return acc + searchQueryFilterContribution(filters.q)
      }
      if (key === 'bidStatus') {
        // bidStatus is now a single string ('winning' | 'losing' |
        // 'no_bid_yet') — undefined means "All" (no filter). Counts as one
        // filter group when set, zero otherwise.
        return acc + (filters.bidStatus ? 1 : 0)
      }
      if (key === 'closesIn') {
        return acc + (filters.closesIn ? 1 : 0)
      }
      if (key === 'msrpPercentage') {
        return acc + numericValueFilterContribution(filters.msrpPercentage)
      }
      if (key === 'categoryThreshold') {
        // Threshold is a modifier of the category filter, not an independent
        // filter choice; don't count it separately in the active-filter total.
        return acc
      }
      if (key === 'problemFreeOrderRate') {
        return (
          acc + numericValueFilterContribution(filters.problemFreeOrderRate)
        )
      }
      if (key === 'endTimeRange') {
        // Checked before isStringList below: a fully-populated endTimeRange
        // (both bounds non-null strings, the common case — schedule
        // click-through always sets both) also satisfies isStringList, and
        // would otherwise be counted by array length (2) instead of as the
        // single range filter it is.
        return acc + rangeContribution(filters.endTimeRange)
      }

      const value = filters[key]
      if (typeof value === 'boolean') {
        return acc + booleanFilterContribution(value)
      }
      if (isStringList(value)) {
        return acc + filterContribution(value)
      }
      if (isRange(value)) {
        return acc + rangeContribution(value)
      }

      // This should only ever happen if someone adds a new filter type, but
      // has not yet added support for it here.
      // The result of this failure is the `N filters applied` messaging will
      // fail to include the logged filter key in the counting (which, if
      // there aren't other applied filters, will also cause it to look as if
      // there was no filter).
      // This log is intentionally `info` instead of `warn` because if we
      // were to get into a situation where this message was being logged,
      // it would be logged repeatedly, and we do not want to flood DataDog
      // with noisy warnings - we just want to make sure this info is available
      // for anyone actively searching for it.

      // eslint-disable-next-line no-console
      console.info('Filter not included in getActiveFilterCount', {
        key,
        value,
      })
      return acc
    },
    0
  )
}

export default calculateActiveFilterCount
